import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  User, Mail, Phone, MapPin, LogOut, Save, Loader2, 
  Wallet, Shield, Calendar, Edit2, Lock, Eye, EyeOff
} from "lucide-react";
import { clearSessionToken } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage({ user, setUser }) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [formData, setFormData] = useState({
    name: user.name || "",
    phone: user.phone || "",
    address_line1: user.address_line1 || "",
    address_line2: user.address_line2 || "",
    city: user.city || "",
    state: user.state || "",
    pincode: user.pincode || ""
  });

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      setWallet(response.data);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      clearSessionToken();
      window.location.href = "/login";
    } catch (error) {
      console.error("Error logging out:", error);
      clearSessionToken();
      window.location.href = "/login";
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, formData);
      toast.success("Profile updated successfully");
      setIsEditing(false);
      if (setUser) {
        setUser(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!passwordData.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setIsSavingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success("Password changed successfully");
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <Sidebar user={user} activePath="/profile" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64 overflow-x-hidden max-w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground mb-1">My Profile</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage your account settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Info Card */}
            <Card className="border-0 shadow-card overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-[#F2337B]/10 to-[#F6D003]/10 pb-20 relative">
                <div className="absolute -bottom-12 left-6">
                  <Avatar className="h-24 w-24 ring-4 ring-white shadow-xl">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] text-white text-3xl font-bold">
                      {user.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="absolute top-4 right-4">
                  <Badge className={`rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    <Shield className="w-3 h-3 mr-1" />
                    {user.role === 'admin' ? (user.is_super_admin ? 'Super Admin' : 'Admin') : 'Franchisee'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-16 pb-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-2xl">{user.name}</h2>
                    <p className="text-muted-foreground flex items-center gap-2 mt-1">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </p>
                    {user.username && (
                      <p className="text-muted-foreground flex items-center gap-2 mt-1">
                        <User className="w-4 h-4" />
                        @{user.username}
                      </p>
                    )}
                  </div>
                  <Button
                    variant={isEditing ? "default" : "outline"}
                    className="rounded-full"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    {isEditing ? "Cancel" : "Edit Profile"}
                  </Button>
                </div>

                {/* Editable Fields */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        Full Name
                      </Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={!isEditing}
                        className={!isEditing ? "bg-muted" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={!isEditing}
                        placeholder="+91 9876543210"
                        className={!isEditing ? "bg-muted" : ""}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#F2337B]" />
                      Delivery Address
                    </h4>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Address Line 1</Label>
                        <Input
                          value={formData.address_line1}
                          onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                          disabled={!isEditing}
                          placeholder="Shop/Building name, Street address"
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Address Line 2</Label>
                        <Input
                          value={formData.address_line2}
                          onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                          disabled={!isEditing}
                          placeholder="Landmark, Area (optional)"
                          className={!isEditing ? "bg-muted" : ""}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            disabled={!isEditing}
                            placeholder="City"
                            className={!isEditing ? "bg-muted" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={formData.state}
                            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                            disabled={!isEditing}
                            placeholder="State"
                            className={!isEditing ? "bg-muted" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Pincode</Label>
                          <Input
                            value={formData.pincode}
                            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                            disabled={!isEditing}
                            placeholder="400001"
                            className={!isEditing ? "bg-muted" : ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="flex justify-end pt-4">
                      <Button onClick={handleSave} disabled={isSaving} className="rounded-full">
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Change Password Card */}
            {user.username && (
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Lock className="w-5 h-5 text-[#F2337B]" />
                      Change Password
                    </CardTitle>
                    {!isChangingPassword && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => setIsChangingPassword(true)}
                      >
                        Change
                      </Button>
                    )}
                  </div>
                </CardHeader>
                {isChangingPassword && (
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          placeholder="Enter new password (min 6 characters)"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1 rounded-full"
                        onClick={() => {
                          setIsChangingPassword(false);
                          setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 rounded-full"
                        onClick={handleChangePassword}
                        disabled={isSavingPassword}
                      >
                        {isSavingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                        Update Password
                      </Button>
                    </div>
                  </CardContent>
                )}
                {!isChangingPassword && (
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      You can change your password here. Make sure to use a strong password with at least 6 characters.
                    </p>
                  </CardContent>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar Cards */}
          <div className="space-y-6">
            {/* Wallet Card */}
            {user.role === 'franchisee' && (
              <Card className="border-0 shadow-card overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-r from-[#2ECC71] to-[#27AE60] p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <Wallet className="w-6 h-6" />
                      <span className="font-semibold">Wallet Balance</span>
                    </div>
                    <p className="text-3xl font-heading">
                      ₹{wallet.balance?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Info Card */}
            <Card className="border-0 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#087DDF]" />
                  Account Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Account Type</span>
                  <span className="font-medium capitalize">{user.role}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className="bg-green-100 text-green-700 rounded-full">Active</Badge>
                </div>
                {user.created_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Member Since</span>
                    <span className="font-medium">
                      {new Date(user.created_at).toLocaleDateString('en-IN', { 
                        year: 'numeric', 
                        month: 'short' 
                      })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logout Card */}
            <Card className="border-0 shadow-card border-red-100">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2 text-gray-700">Sign Out</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Sign out of your account on this device.
                </p>
                <Button 
                  variant="outline"
                  className="w-full justify-center gap-2 text-red-500 border-red-200 hover:text-white hover:bg-red-500 hover:border-red-500 rounded-xl transition-all"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
